import { defineComponent } from "@openuidev/lang-core";
import { z } from "zod";

// Maths widgets. See ../widgets.ts for the rules all widgets follow.

export const NumberLine = defineComponent({
  name: "NumberLine",
  description:
    "An interactive number line the child can place a point on. Use for ordering, fractions and negative numbers.",
  props: z.object({
    min: z.number(),
    max: z.number(),
    step: z.number().positive(),
    label: z.string().max(80).optional(),
  }),
  component: null,
});

export const FractionBars = defineComponent({
  name: "FractionBars",
  description:
    "Side-by-side bars split into equal parts, some shaded. Use to compare or build fractions.",
  props: z.object({
    bars: z
      .array(
        z.object({
          parts: z.number().int().min(1).max(24),
          shaded: z.number().int().min(0).max(24),
        }),
      )
      .min(1)
      .max(4),
  }),
  component: null,
});

/** A part label on an area model side: "20", "3", "1/2", "x". */
const areaPart = z.string().trim().min(1).max(12);

export const AreaModel = defineComponent({
  name: "AreaModel",
  description:
    'A rectangle split into a grid: row parts down the side, column parts along the top, e.g. ["20", "3"] by ["10", "4"] for 23 x 14, or fraction-of-fraction. The child taps a cell to see its partial product written as an expression ("20 x 10"), never its value: computing it is the child\'s job.',
  props: z.object({
    rows: z.array(areaPart).min(1).max(4),
    columns: z.array(areaPart).min(1).max(4),
    caption: z.string().max(80).optional(),
  }),
  component: null,
});

/** The number of "your turn" blanks EquationSteps may end with. */
export const MAX_EQUATION_BLANKS = 6;

export const EquationSteps = defineComponent({
  name: "EquationSteps",
  description:
    'Worked steps shown one under the other, aligned on "=": the child\'s own steps, or the start of a worked example. Each step is plain-text maths ("3x + 5 = 20") with an optional short note ("subtract 5 from both sides"). To leave the end to the child, stop before the answer and set blanks to the number of "your turn" lines; never include the remaining steps.',
  props: z.object({
    steps: z
      .array(
        z.object({
          expression: z.string().trim().min(1).max(120),
          note: z.string().max(120).optional(),
        }),
      )
      .min(1)
      .max(12),
    blanks: z.number().int().min(0).max(MAX_EQUATION_BLANKS).optional(),
  }),
  component: null,
});

/** Place-value columns, largest first. The chart always shows them in this order. */
export const PLACE_VALUE_COLUMNS = [
  "thousands",
  "hundreds",
  "tens",
  "ones",
  "tenths",
  "hundredths",
] as const;

export const PlaceValueChart = defineComponent({
  name: "PlaceValueChart",
  description:
    "A place-value chart the child builds a number in, one digit per column with + and - buttons, then shares with you as text. Use for reading, writing and comparing numbers and decimals. columns are taken from thousands, hundreds, tens, ones, tenths, hundredths; digits (optional, 0-9, one per column) sets a starting number.",
  props: z.object({
    columns: z.array(z.enum(PLACE_VALUE_COLUMNS)).min(1).max(PLACE_VALUE_COLUMNS.length),
    digits: z.array(z.number().int().min(0).max(9)).max(PLACE_VALUE_COLUMNS.length).optional(),
  }),
  component: null,
});

/** Shapes ShapeExplorer can draw; each brings its own controls. */
export const EXPLORER_SHAPES = [
  "rectangle",
  "parallelogram",
  "rhombus",
  "trapezium",
  "triangle",
] as const;

/** What ShapeExplorer may mark on the shape. */
export const EXPLORER_FACTS = ["lengths", "marks", "diagonals"] as const;

const gridSize = z.number().int().min(1).max(10);

export const ShapeExplorer = defineComponent({
  name: "ShapeExplorer",
  description:
    "A shape on a squared grid that the child reshapes with buttons, to see what stays true and what changes, then shares with you as text. shape is rectangle, parallelogram, rhombus, trapezium or triangle. size (optional, in grid squares) sets the start: width (the base; for a rhombus, the across diagonal), height (for a rhombus, the up diagonal), lean (-10 to 10: how far right of the bottom left corner the top starts, so 0 stands straight up; for a triangle, where the top corner sits) and top (a trapezium's top side; it is isosceles when lean is half of width minus top). show (optional, default [\"marks\"]) picks what is drawn: marks (equal-side ticks, right-angle squares, parallel arrows), lengths (only sides along the grid lines, in squares), diagonals. It never names the shape and never shows an angle measure: naming it is the child's job. Do not start it on the shape or lengths the question asks for.",
  props: z.object({
    shape: z.enum(EXPLORER_SHAPES),
    size: z
      .object({
        width: gridSize.optional(),
        height: gridSize.optional(),
        lean: z.number().int().min(-10).max(10).optional(),
        top: gridSize.optional(),
      })
      .optional(),
    show: z.array(z.enum(EXPLORER_FACTS)).max(EXPLORER_FACTS.length).optional(),
  }),
  component: null,
});

export const mathWidgets = [
  NumberLine,
  FractionBars,
  AreaModel,
  EquationSteps,
  PlaceValueChart,
  ShapeExplorer,
] as const;
